import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { commissionAdvanceApi } from "../../../../modules/commissions/advance/api";
import {
  CreateCommissionAdvance,
  CreateCommissionAdvanceCancellation,
  UpdateCommissionAdvance,
} from "../../../../modules/commissions/advance/types";
import type { UUID } from "../../../../typings/global";
import { EntityIdObject, EntityObject } from "../../../types";
import { HttpStatus } from "../../http";
import { COMMISSION_ADVANCE_QUERY_KEYS } from "./queryKeys";

export const useGetCommissionAdvance = (id: UUID) => {
  return useQuery({
    queryKey: COMMISSION_ADVANCE_QUERY_KEYS.getCommissionAdvance(id),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { status, data } = await commissionAdvanceApi.getCommissionAdvance({ id });

      // A contract without an advance answers 204 with an empty body, and React Query rejects undefined data
      return status === HttpStatus.NO_CONTENT ? null : data;
    },
  });
};

export const useCreateCommissionAdvance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_ADVANCE_QUERY_KEYS.createCommissionAdvance(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateCommissionAdvance>) => {
      const { data } = await commissionAdvanceApi.createCommissionAdvance(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(COMMISSION_ADVANCE_QUERY_KEYS.getCommissionAdvance(mutatedPayload.id), data);
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionAdvance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_ADVANCE_QUERY_KEYS.updateCommissionAdvance(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateCommissionAdvance>) => {
      const { data } = await commissionAdvanceApi.updateCommissionAdvance(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(COMMISSION_ADVANCE_QUERY_KEYS.getCommissionAdvance(mutatedPayload.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useCreateCommissionAdvanceCancellation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_ADVANCE_QUERY_KEYS.createCommissionAdvanceCancellation(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateCommissionAdvanceCancellation>) => {
      const { data } = await commissionAdvanceApi.createCommissionAdvanceCancellation(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(COMMISSION_ADVANCE_QUERY_KEYS.getCommissionAdvance(mutatedPayload.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionAdvanceCancellation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_ADVANCE_QUERY_KEYS.deleteCommissionAdvanceCancellation(),
    mutationFn: (payload: EntityIdObject) => {
      return commissionAdvanceApi.deleteCommissionAdvanceCancellation(payload);
    },
    onSuccess: async (_, mutatedPayload) => {
      await queryClient.invalidateQueries({
        queryKey: COMMISSION_ADVANCE_QUERY_KEYS.getCommissionAdvance(mutatedPayload.id),
      });
      Notification.itemUpdatedNotification();
    },
  });
};
